import { useCallback } from 'react'

import { ReportSection, Stub, ReportDetailsRoot } from 'app/common/templates-next/report-template'
import { ReportStatusIndicator } from 'app/components/common/reports/v3/ReportStatusIndicator'
import { Loading } from 'app/common/templates-next/page-template'
import { useReportResult } from 'app/dataProviders/reports/hooks'
import { ReportType } from 'app/dataTypes/reports'
import { useLazyLoading } from 'app/components/common/reports/v3/hooks/useLazyLoading'

import {
  ReportFinancesSummaryData,
  FinancesSummaryDetailsGroup,
} from 'app/dataTypes/reports/data/common/financesSummary'

import { TableGroupByDates } from './groupByDates/TableGroupByDates'
import { TableGroupByStudents } from './groupByStudents/TableGroupByStudents'
import { HeaderBar } from './HeaderBar'

const dataReplacement = (
  data: ReportFinancesSummaryData | null,
  visiblePortion: number,
): ReportFinancesSummaryData | null => {
  if (!data) return null
  const { groupBy } = data
  return groupBy === FinancesSummaryDetailsGroup.Dates
    ? {
        ...data,
        rows: data.rows.slice(0, visiblePortion),
      }
    : {
        ...data,
        rows: data.rows.slice(0, visiblePortion),
      }
}

type Props = {
  reportId: null | string
  onReportId: (reportId: string | null) => void
}

export const Result = ({ reportId, onReportId }: Props) => {
  const resetReport = useCallback(() => {
    onReportId(null)
  }, [onReportId])

  const {
    requestResult,
    data: fullData,
    loading: pending,
  } = useReportResult(reportId, ReportType.FinancesSummary)

  const data = useLazyLoading({
    data: fullData,
    dataReplacement,
    skip: fullData?.groupBy === FinancesSummaryDetailsGroup.Dates,
  })

  const header = !reportId ? '' : 'Отчет «Финансы: общий»'

  return (
    <ReportSection
      header={header}
      headerBar={<HeaderBar reportID={reportId} reportReady={!!data && !pending} />}
      fullscreenEnabled={!!data && !pending}
      skipPadding
    >
      {reportId && (
        <ReportStatusIndicator reportID={reportId} onFail={resetReport} onSuccess={requestResult} />
      )}

      {!data && !reportId && <Stub />}

      {reportId && pending && <Loading />}

      {data && (
        <ReportDetailsRoot header={data.title}>
          {data.groupBy === FinancesSummaryDetailsGroup.Dates && (
            <TableGroupByDates data={data.rows} pending={pending} />
          )}
          {data.groupBy === FinancesSummaryDetailsGroup.Students && (
            <TableGroupByStudents data={data.rows} totals={data.totals} pending={pending} />
          )}
        </ReportDetailsRoot>
      )}
    </ReportSection>
  )
}
